// ABOUTME: Dialog asking which dealership the catalogue, the cart or an offer works in.
// ABOUTME: Lists the selectable dealerships, plus "None (company)" where allowed; a change warns the catalogue reloads.

import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@ds/coolshop';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import type { DealershipChoice, SelectableDealerships } from '../types';
import { DealershipSelect } from './DealershipSelect';

type DealershipPickerDialogProps = {
    open: boolean;
    selectable: SelectableDealerships;
    /** the choice to preselect: the user's last one */
    initialChoice?: DealershipChoice;
    /** a number beside each option, e.g. the cart's lines in that dealership */
    optionCounts?: ReadonlyMap<string | undefined, number>;
    /** warns, below the select, that the catalogue on screen reloads in the new dealership */
    reloadsCatalogue?: boolean;
    onPick: (choice: DealershipChoice) => void;
    onCancel: () => void;
};

export function DealershipPickerDialog({
    open,
    selectable,
    initialChoice,
    optionCounts,
    reloadsCatalogue = false,
    onPick,
    onCancel,
}: DealershipPickerDialogProps) {
    const { formatMessage } = useIntl();
    // undefined is "none" for the users who may work without a dealership, "nothing picked" for the
    // others.
    const [value, setValue] = useState<string | undefined>();

    // The last choice is preselected only while it is still open to the user: one of their
    // dealerships (not a link removed since), or "none" while they may work without one. Otherwise
    // the first dealership.
    useEffect(() => {
        if (!open) return;
        if (initialChoice?.kind === 'none' && selectable.allowNone) {
            setValue(undefined);
            return;
        }
        const initialDealershipId =
            initialChoice?.kind === 'dealership' ? initialChoice.dealershipId : undefined;
        const initialIsSelectable = selectable.dealerships.some(d => d.id === initialDealershipId);
        setValue(initialIsSelectable ? initialDealershipId : selectable.dealerships[0]?.id);
    }, [open, initialChoice, selectable]);

    const canConfirm = !!value || selectable.allowNone;
    const confirm = () =>
        onPick(value ? { kind: 'dealership', dealershipId: value } : { kind: 'none' });

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle>
                {formatMessage({
                    id: 'dealership.picker.title',
                    defaultMessage: 'Choose the dealership',
                })}
            </DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} paddingTop={1}>
                    <Typography variant="body2">
                        {formatMessage({
                            id: 'dealership.picker.body',
                            defaultMessage:
                                'The catalogue, its prices and the cart follow the dealership you work in.',
                        })}
                    </Typography>
                    <DealershipSelect
                        fullWidth
                        selectable={selectable}
                        label={formatMessage({
                            id: 'dealership.picker.label',
                            defaultMessage: 'Dealership',
                        })}
                        value={value}
                        onChange={setValue}
                        optionCounts={optionCounts}
                    />
                    {reloadsCatalogue && (
                        <Alert severity="info">
                            {formatMessage({
                                id: 'dealership.picker.reloadsCatalogue',
                                defaultMessage:
                                    'Changing the dealership reloads the catalogue in the new one.',
                            })}
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Box display="flex" gap={1}>
                    <Button variant="text" onClick={onCancel}>
                        {formatMessage({ id: 'common.cancel', defaultMessage: 'Cancel' })}
                    </Button>
                    <Button variant="contained" onClick={confirm} disabled={!canConfirm}>
                        {formatMessage({
                            id: 'dealership.picker.confirm',
                            defaultMessage: 'Continue',
                        })}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}

export default DealershipPickerDialog;
